/**
 * Tag snippets (DECISIONS #137): operator HTML from the remote config,
 * injected once per page load (On Load) when the main thread is idle, only
 * with consent. Markup-parsed <script>s never run, so each one is rebuilt;
 * an external script without `async` holds the rest of its snippet until it
 * loads or fails, the order a parser would give them.
 */
import type { TagSnippet } from '@l3ns/shared/types';
import { safe } from '../core/safe';
import { st } from '../core/state';
import { urlOk } from './mappings';
import { idle } from './util';

/** A runnable copy of a parsed (inert) script: same attributes and source. */
function fresh(old: HTMLScriptElement): HTMLScriptElement {
	const s = document.createElement('script');
	for (const a of Array.from(old.attributes)) s.setAttribute(a.name, a.value);
	s.text = old.text;
	return s;
}

export function inject(html: string): void {
	if (!st.consent_) return;
	const t = document.createElement('template');
	t.innerHTML = html;
	const nodes = Array.from(t.content.childNodes);
	const next = safe((): void => {
		const n = nodes.shift();
		if (!n) return;
		const to = document.body || document.head;
		if (n.nodeName == 'SCRIPT') {
			const old = n as HTMLScriptElement;
			const s = fresh(old);
			if (old.src && !old.hasAttribute('async')) {
				s.async = false;
				s.onload = s.onerror = next;
				to.appendChild(s);
				return;
			}
			to.appendChild(s);
		} else {
			// scripts nested in markup run too (inserted already rebuilt)
			if (n instanceof Element)
				for (const old of Array.from(n.querySelectorAll('script'))) old.replaceWith(fresh(old));
			to.appendChild(n);
		}
		next();
	});
	next();
}

export function runSnippets(list: TagSnippet[]): void {
	for (const s of list) if (!s.urls || s.urls.some(urlOk)) idle(safe(() => inject(s.html)));
}
